<template>
  <main id="quote" class="page-container" v-if="$route.name === 'quotes'">
    <!-- Messaggi -->
    <!-- <p
      v-if="errorMessage"
      :class="{ 'error-message': true, show: errorMessage }"
    >
      {{ errorMessage }}
    </p>
    <p
      v-if="successMessage"
      :class="{ 'success-message': true, show: successMessage }"
    >
      {{ successMessage }}
    </p> -->

    <div class="table-container-quote">
      <!-- LOADER OVERLAY -->
      <div
        v-if="isLoading"
        class="loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <div class="spinner" role="status"></div>
        <p>Carico i preventivi…</p>
      </div>

      <!-- Toolbar -->
      <div class="quotes-toolbar">
        <router-link to="/home/quotes/add-quote" class="primary-btn">
          <i class="fa-solid fa-plus"></i> Nuovo preventivo
        </router-link>
        <button
          class="refresh-btn"
          :class="{ refreshed }"
          @click="handleRefresh"
          :disabled="refreshing"
          title="Aggiorna"
        >
          <i
            :class="[
              'fa-solid',
              refreshed ? 'fa-check' : 'fa-arrows-rotate',
              { 'fa-spin': refreshing },
            ]"
          ></i>
          <span>{{
            refreshing ? "Aggiorna…" : refreshed ? "Aggiornato!" : "Aggiorna"
          }}</span>
        </button>
      </div>

      <!-- Tabella -->
      <div class="tbl-wrap" :class="{ dimmed: isLoading }">
        <table class="tbl tbl-elenco quote-table">
          <thead>
            <tr>
              <th>Cliente</th>
              <th class="col-no-mobile">Totale da pagare</th>
              <th class="col-p2">Data</th>
              <th class="col-p3">Indirizzo di consegna</th>
              <th class="col-stato-largo">Conferma</th>
              <th class="col-stato-largo">Pagamento</th>
              <th class="col-stato-largo">Spedizione</th>
              <th class="col-stato-stretto">Stato</th>
              <th class="doc-cell col-no-mobile">Documenti</th>
              <th>Azioni</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="quote in allQuotes"
              :key="quote.id"
              class="quote-row riga-cliccabile"
              tabindex="0"
              @click="apriDettaglioPreventivo($event, quote)"
              @keydown.enter="apriDettaglioPreventivo($event, quote)"
            >
              <!-- <td>{{ quote.Client.clientName }}</td> -->
              <td
                class="client-cell"
                @mouseenter="onCellEnter(quote.id)"
                @mouseleave="onCellLeave(quote.id)"
              >
                <button
                  class="client-trigger"
                  @focus="onCellEnter(quote.id)"
                  @blur="onCellLeave(quote.id)"
                  @click.stop.prevent="toggleTooltip(quote.id)"
                  @touchstart.stop.prevent="onTriggerTouch(quote.id)"
                  @keydown="onTriggerKeydown($event, quote.id)"
                  :aria-expanded="isTooltipVisible(quote.id)"
                  :aria-describedby="'tt-' + quote.id"
                  type="button"
                >
                  {{ quote.Client?.clientName || "—" }}
                  <span class="sr-only">Apri contatti</span>
                  <span class="client-hint-mobile">Tocca per contatti</span>
                </button>

                <transition name="fade">
                  <div
                    v-if="isTooltipVisible(quote.id)"
                    :id="'tt-' + quote.id"
                    role="tooltip"
                    class="email-tooltip"
                    :class="{ 'no-pointer': lockClickId === quote.id }"
                    @click.stop
                    @touchstart.stop
                  >
                    <!-- CONTENUTO TOOLTIP -->
                    <div class="tooltip-row" v-if="quote?.Client?.email">
                      <span class="label">Email</span>
                      <a :href="'mailto:' + quote.Client.email">{{
                        quote.Client.email
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.email, 'email')"
                        title="Copia email"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div class="tooltip-row" v-if="quote?.Client?.pec">
                      <span class="label">PEC</span>
                      <a :href="'mailto:' + quote.Client.pec">{{
                        quote.Client.pec
                      }}</a>
                      <button
                        class="copy-btn"
                        @click="copyToClipboard(quote.Client.pec, 'pec')"
                        title="Copia PEC"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div class="tooltip-row" v-if="quote?.Client?.phoneNumber">
                      <span class="label">Telefono</span>
                      {{ quote.Client.phoneNumber }}
                      <button
                        class="copy-btn"
                        @click="
                          copyToClipboard(quote.Client.phoneNumber, 'phone')
                        "
                        title="Copia numero di telefono"
                      >
                        <i class="fa-regular fa-copy"></i>
                      </button>
                    </div>

                    <div
                      class="tooltip-row muted"
                      v-if="
                        !quote?.Client?.email &&
                        !quote?.Client?.pec &&
                        !quote?.Client?.phoneNumber
                      "
                    >
                      Nessun contatto disponibile.
                    </div>
                  </div>
                </transition>
                <!-- [sc-48893] colonne Data e Totale nascoste: i valori
                     scendono sotto il nome del cliente -->
                <span class="cella-sotto">
                  <span class="cella-sotto__data">{{
                    formatToLocalDate(quote.date)
                  }}</span>
                  <span class="cella-sotto__totale">
                    <template v-if="daRestituire(quote.realTotalPrice) > 0">
                      Da restituire
                      {{
                        formatPriceToPrint(daRestituire(quote.realTotalPrice))
                      }}
                    </template>
                    <template v-else>{{
                      formatPriceToPrint(quote.realTotalPrice)
                    }}</template>
                  </span>
                </span>
              </td>

              <td class="col-no-mobile">
                <!-- Pagato più del dovuto: è un credito, non un importo
                     negativo da saldare [sc-48768]. -->
                <span
                  v-if="daRestituire(quote.realTotalPrice) > 0"
                  class="da-restituire"
                  title="Il cliente ha pagato più del totale"
                >
                  Da restituire
                  {{ formatPriceToPrint(daRestituire(quote.realTotalPrice)) }}
                </span>
                <template v-else>
                  {{ formatPriceToPrint(quote.realTotalPrice) }}
                </template>
              </td>
              <td class="date-cell col-p2">
                {{ formatToLocalDate(quote.date) }}
              </td>
              <td class="address-cell col-p3">
                <a
                  v-if="quote.location && quote.location.link"
                  :href="quote.location.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="custom-link"
                  >{{ formatAddress(quote.location.address) }}</a
                >
                <span v-else>Indirizzo non disponibile</span>
              </td>

              <!-- Conferma, Pagamento, Spedizione: una colonna ciascuno da
                   1200px in su, impilati nella colonna «Stato» sotto [sc-48893] -->
              <td
                v-for="c in campiStato(quote)"
                :key="c.key"
                class="state-container col-stato-largo"
                data-no-dettaglio
              >
                <StatusSelect
                  :modelValue="quote[c.key]"
                  :options="c.options"
                  :disabled="c.disabled"
                  @update:modelValue="(val) => cambiaStato(quote, c.key, val)"
                />
                <div
                  v-if="c.key === 'state' && consegnaParziale(quote)"
                  class="delivery-progress"
                >
                  <span class="delivery-progress__qty">
                    {{ consegnaParziale(quote) }}
                  </span>
                </div>
              </td>
              <td class="state-container col-stato-stretto" data-no-dettaglio>
                <div
                  v-for="c in campiStato(quote)"
                  :key="c.key"
                  class="stato-impilato"
                >
                  <span class="stato-impilato__label">{{ c.label }}</span>
                  <StatusSelect
                    :modelValue="quote[c.key]"
                    :options="c.options"
                    :disabled="c.disabled"
                    @update:modelValue="(val) => cambiaStato(quote, c.key, val)"
                  />
                </div>
                <div v-if="consegnaParziale(quote)" class="delivery-progress">
                  <span class="delivery-progress__qty">
                    {{ consegnaParziale(quote) }}
                  </span>
                </div>
              </td>

              <!-- Documenti -->
              <td class="doc-cell col-no-mobile">
                <button
                  class="doc-btn doc-btn--pdf"
                  type="button"
                  title="Preventivo PDF"
                  @click="openDocumentsModal(quote, 'pdf')"
                >
                  <i class="fa-solid fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
                <button
                  class="doc-btn doc-btn--ddt"
                  type="button"
                  title="Storico DDT"
                  @click="openDocumentsModal(quote, 'ddt')"
                >
                  <i class="fa-solid fa-truck"></i>
                  <span>DDT</span>
                </button>
              </td>

              <!-- Azioni -->

              <td class="actions-cell">
                <AzioniRiga>
                  <!-- sotto i 720px la colonna Documenti si nasconde: PDF e DDT
                     restano raggiungibili dal menu -->
                  <button
                    class="doc-btn doc-btn--pdf solo-menu"
                    type="button"
                    title="Preventivo PDF"
                    @click="openDocumentsModal(quote, 'pdf')"
                  >
                    <i class="fa-solid fa-file-pdf"></i>
                  </button>
                  <button
                    class="doc-btn doc-btn--ddt solo-menu"
                    type="button"
                    title="Storico DDT"
                    @click="openDocumentsModal(quote, 'ddt')"
                  >
                    <i class="fa-solid fa-truck"></i>
                  </button>
                  <router-link
                    v-if="!showArchived"
                    :to="`/home/quotes/edit-quote/${quote.id}`"
                    class="icons-button"
                    title="Modifica preventivo"
                    aria-label="Modifica preventivo"
                  >
                    <i class="fa-solid fa-pen-to-square"></i>
                  </router-link>

                  <button
                    v-if="canEdit(quote) || !showArchived"
                    class="icons-button"
                    @click="openDiscountModal(quote)"
                    title="Modifica prezzo o aggiungi acconto"
                    aria-label="Modifica prezzo o aggiungi acconto"
                  >
                    <i class="fa-regular fa-percent"></i>
                  </button>

                  <button
                    class="icons-button"
                    type="button"
                    :class="{
                      'icons-button--reso-done':
                        quote.state === 'Consegnato' && quote.hasReso,
                    }"
                    :disabled="quote.state === 'Consegnato' && quote.hasReso"
                    :title="
                      quote.state === 'Consegnato' && quote.hasReso
                        ? 'Reso effettuato — nessuna azione disponibile'
                        : 'Genera DDT'
                    "
                    @click="openDdtModal(quote)"
                  >
                    <i class="fa-solid fa-truck"></i>
                    <i
                      v-if="quote.state === 'Consegnato' && quote.hasReso"
                      class="fa-solid fa-rotate-left ddt-btn-reso-badge"
                    ></i>
                  </button>
                  <button
                    v-if="!showArchived"
                    class="icons-button icons-button--danger"
                    @click="openArchiveModal(quote)"
                    title="Archivia preventivo"
                    aria-label="Archivia preventivo"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>

                  <button
                    v-else-if="canRestore(quote)"
                    class="icons-button icons-button--danger"
                    @click="openRestoreModal(quote)"
                    title="Ripristina preventivo"
                    aria-label="Ripristina preventivo"
                  >
                    <i class="fa-solid fa-trash-arrow-up"></i>
                  </button>
                </AzioniRiga>
              </td>
            </tr>
            <tr v-if="allQuotes.length === 0 && !isLoading">
              <td colspan="10" class="no-result">Nessun preventivo trovato.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination / search / archived toggle -->
      <div class="pagination">
        <div class="pagination-left">
          <div class="search-container">
            <i
              v-if="!searchTerm"
              class="fa-solid fa-magnifying-glass search-icon"
            ></i>
            <input
              type="text"
              v-model="searchTerm"
              @keyup.enter="onSearch"
              placeholder="Cerca preventivi..."
              class="search-box"
            />

            <button
              v-if="searchTerm"
              @click="onResetSearch"
              class="clear-search-button"
              :disabled="isLoading"
            >
              <i class="fa-solid fa-times"></i>
            </button>
          </div>

          <label v-if="hasArchived" class="archived-toggle tooltip">
            <input
              type="checkbox"
              v-model="showArchived"
              @change="onToggleArchived($event)"
              class="visually-hidden"
              :aria-label="
                showArchived
                  ? 'Torna ai preventivi attivi'
                  : 'Mostra preventivi archiviati'
              "
            />
            <i
              :class="[
                'fa-solid',
                showArchived ? 'fa-file-invoice' : 'fa-box-archive',
                'icons-button',
              ]"
              aria-hidden="true"
            ></i>
            <span class="tooltiptext">
              {{
                showArchived
                  ? "Torna ai preventivi attivi"
                  : "Mostra preventivi archiviati"
              }}
            </span>
          </label>
        </div>

        <div class="pagination-right">
          <div class="pagination-info">
            {{ pagination.startItem }}-{{ pagination.endItem }} di
            {{ pagination.totalItems }}
          </div>

          <div class="pagination-controls">
            <button
              class="icons-button"
              @click="goToPage(1)"
              :disabled="isLoading || pagination.currentPage === 1"
              :title="
                isLoading || pagination.currentPage === 1
                  ? null
                  : 'Torna alla prima pagina'
              "
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage - 1)"
              :disabled="isLoading || pagination.currentPage === 1"
              :title="
                isLoading || pagination.currentPage === 1
                  ? null
                  : 'Torna alla pagina precedente'
              "
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>

            <button
              v-for="pageNum in visiblePages"
              :key="pageNum"
              @click="goToPage(pageNum)"
              :class="{ active: pagination.currentPage === pageNum }"
              class="pages-button"
            >
              {{ pageNum }}
            </button>

            <button
              class="icons-button"
              @click="goToPage(pagination.currentPage + 1)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
              :title="
                isLoading || pagination.currentPage === pagination.totalPages
                  ? null
                  : 'Vai alla pagina successiva'
              "
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button
              class="icons-button"
              @click="goToPage(pagination.totalPages)"
              :disabled="
                isLoading || pagination.currentPage === pagination.totalPages
              "
              :title="
                isLoading || pagination.currentPage === pagination.totalPages
                  ? null
                  : `Vai all'ultima pagina`
              "
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- MODALE DOCUMENTI -->
      <VueFinalModal
        v-model="showDocumentsModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content"
      >
        <template #default>
          <!-- [sc-48860] header come nelle altre modali: titolo e X separati dal corpo -->
          <div class="modal__header">
            <h3 class="modal__title">
              {{ docModalSection === "ddt" ? "Storico DDT" : "Preventivo" }}
              #{{ currentQuoteId }}
            </h3>
            <button
              class="modal__close icons-button"
              @click="closeDocumentsModal"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="docs-modal-body">
            <!-- Documenti preventivo (PDF) -->
            <template v-if="docModalSection === 'pdf'">
              <!-- [sc-48860] il PDF si riscarica senza dover modificare il preventivo -->
              <button
                type="button"
                class="document-card regen-pdf-card"
                :disabled="rigenerandoPdf"
                title="Scarica nuovamente il pdf del preventivo"
                @click="rigeneraPdf"
              >
                <span class="card-link">
                  <span class="card-icon-pdf">
                    <i
                      class="fa-solid"
                      :class="
                        rigenerandoPdf ? 'fa-spinner fa-spin' : 'fa-rotate'
                      "
                    ></i>
                  </span>
                  <span class="card-body">
                    <span class="card-title">{{
                      rigenerandoPdf
                        ? "Rigenerazione in corso…"
                        : "Rigenera PDF"
                    }}</span>
                    <span class="card-subtitle"
                      >Scarica nuovamente il pdf del preventivo</span
                    >
                  </span>
                </span>
              </button>

              <transition-group name="card" tag="div" class="documents-grid">
                <div
                  v-for="(doc, i) in filteredDocuments"
                  :key="safeDocKey(doc, i)"
                  class="document-card"
                >
                  <a href="#" class="card-link" @click.prevent="openDoc(doc)">
                    <div class="card-icon-pdf">
                      <i class="fa-solid fa-file-pdf"></i>
                    </div>
                    <div class="card-body">
                      <div class="card-title">
                        {{ formatDocName(doc.name) }}
                      </div>
                      <div class="card-subtitle">
                        Data di creazione:
                        {{ formatToLocalDate(doc.createdAt) }}
                      </div>
                      <div
                        v-if="
                          doc._quoteUpdatedAt &&
                          formatToLocalDateAndHours(doc._quoteUpdatedAt) !==
                            formatToLocalDateAndHours(doc.createdAt)
                        "
                        class="card-subtitle"
                      >
                        Aggiornato il:
                        {{ formatToLocalDateAndHours(doc._quoteUpdatedAt) }}
                      </div>
                    </div>
                  </a>
                </div>

                <div v-if="filteredDocuments.length === 0" class="no-result">
                  Nessun documento trovato
                </div>
              </transition-group>
            </template>

            <!-- Storico DDT -->
            <template v-if="docModalSection === 'ddt'">
              <div class="ddt-history">
                <div v-if="selectedDdtDocuments.length === 0" class="no-result">
                  Nessun DDT trovato per questo preventivo.
                </div>
                <template v-if="ddtDocumentsByCausale.consegna.length > 0">
                  <div class="ddt-history__group-label">
                    Consegne
                    <span class="ddt-history__group-count"
                      >({{ ddtDocumentsByCausale.consegna.length }})</span
                    >
                  </div>
                  <div
                    v-for="(doc, idx) in ddtDocumentsByCausale.consegna"
                    :key="doc.id"
                    class="ddt-history__row"
                  >
                    <a
                      href="#"
                      class="ddt-history__link"
                      @click.prevent="openDoc(doc)"
                    >
                      <i class="fa-solid fa-file-arrow-down"></i>
                      DDT N° {{ doc._ddtId }}
                      <span class="ddt-history__seq"
                        >Consegna {{ idx + 1 }}</span
                      >
                    </a>
                    <span class="ddt-history__date">{{
                      formatToLocalDate(doc.createdAt)
                    }}</span>
                  </div>
                </template>
                <template v-if="ddtDocumentsByCausale.reso.length > 0">
                  <div class="ddt-history__group-label">Resi</div>
                  <div
                    v-for="doc in ddtDocumentsByCausale.reso"
                    :key="doc.id"
                    class="ddt-history__row"
                  >
                    <a
                      href="#"
                      class="ddt-history__link"
                      @click.prevent="openDoc(doc)"
                    >
                      <i class="fa-solid fa-file-arrow-down"></i>
                      DDT N° {{ doc._ddtId }}
                    </a>
                    <span class="ddt-history__date">{{
                      formatToLocalDate(doc.createdAt)
                    }}</span>
                  </div>
                </template>
                <template v-if="ddtDocumentsByCausale.ritiro.length > 0">
                  <div class="ddt-history__group-label">Ritiri</div>
                  <div
                    v-for="doc in ddtDocumentsByCausale.ritiro"
                    :key="doc.id"
                    class="ddt-history__row"
                  >
                    <a
                      href="#"
                      class="ddt-history__link"
                      @click.prevent="openDoc(doc)"
                    >
                      <i class="fa-solid fa-file-arrow-down"></i>
                      DDT N° {{ doc._ddtId }}
                    </a>
                    <span class="ddt-history__date">{{
                      formatToLocalDate(doc.createdAt)
                    }}</span>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE ARCHIVIAZIONE -->
      <VueFinalModal
        v-model="showArchiveModal"
        :clickToClose="!archiving"
        :escToClose="!archiving"
        classes="documents-modal"
        content-class="confirm-modal-content modal--archive"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeArchiveModal"
            :disabled="archiving"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Archivia preventivo</h3>
          <p class="modal__subtitle">
            Archiviare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong
            >?
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeArchiveModal"
              :disabled="archiving"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmArchive"
              :disabled="archiving"
            >
              <span v-if="archiving">Archivia…</span>
              <span v-else>Archivia</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE RIPRISTINO -->
      <VueFinalModal
        v-model="showRestoreModal"
        :clickToClose="!restoring"
        :escToClose="!restoring"
        classes="documents-modal"
        content-class="confirm-modal-content modal--restore"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="closeRestoreModal"
            :disabled="restoring"
            title="Chiudi"
            aria-label="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">Ripristina preventivo</h3>
          <p class="modal__subtitle">
            Ripristinare il preventivo
            <strong>#{{ currentQuoteId }}</strong>
            di
            <strong>{{ selectedQuote?.Client?.clientName }}</strong> dagli
            archivi?
          </p>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="closeRestoreModal"
              :disabled="restoring"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="confirmRestore"
              :disabled="restoring"
            >
              <span v-if="restoring">Ripristino…</span>
              <span v-else>Ripristina</span>
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE CONFERMA CAMBIO STATO -->
      <VueFinalModal
        v-model="showConfirmModal"
        :clickToClose="!confirmBusy"
        :escToClose="!confirmBusy"
        classes="documents-modal"
        content-class="confirm-modal-content modal--state"
      >
        <template #default>
          <button
            class="modal__close icons-button"
            @click="handleConfirmCancel"
            :disabled="confirmBusy"
            title="Chiudi"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>

          <h3 class="modal__title">{{ confirmTitle }}</h3>
          <p class="modal__subtitle" v-html="confirmMessage"></p>
          <!-- [sc-49300] acconto troppo basso per consegnare anche un solo pezzo -->
          <p v-if="confirmBloccato" class="confirm-deposit-over-limit">
            {{ confirmBloccato }}
          </p>

          <div v-if="confirmRequiresDeposit" class="confirm-deposit-field">
            <!-- [sc-49299] importo e metodo affiancati, come in «Modifica
                 prezzo o aggiungi acconto» -->
            <div class="confirm-deposit-campi">
              <div class="confirm-deposit-campo">
                <label class="confirm-deposit-label">Importo acconto (€)</label>
                <input
                  type="number"
                  v-model.number="confirmDepositAmount"
                  min="0"
                  :max="pendingQuoteRemainingBalance"
                  step="0.01"
                  placeholder="0.00"
                  class="confirm-deposit-input"
                />
              </div>
              <!-- come è stato pagato l'acconto -->
              <div class="confirm-deposit-campo confirm-deposit-metodo">
                <label class="confirm-deposit-label">Metodo di pagamento</label>
                <StatusSelect
                  v-model="confirmDepositMethod"
                  :options="opzioniMetodoAcconto"
                  placeholder="Scegli il metodo..."
                  placeholderIcon="fa-wallet"
                />
              </div>
            </div>
            <p
              v-if="
                (Number(confirmDepositAmount) || 0) >
                pendingQuoteRemainingBalance
              "
              class="confirm-deposit-over-limit"
            >
              L'acconto supera il residuo da saldare ({{
                formatPriceToPrint(pendingQuoteRemainingBalance)
              }}).
            </p>
          </div>

          <div class="modal-actions">
            <button
              class="secondary-btn"
              @click="handleConfirmCancel"
              :disabled="confirmBusy"
            >
              Annulla
            </button>
            <button
              class="primary-btn confirm-btn"
              @click="handleConfirmOK(false)"
              :disabled="
                confirmBusy ||
                Boolean(confirmBloccato) ||
                (confirmRequiresDeposit &&
                  ((Number(confirmDepositAmount) || 0) >
                    pendingQuoteRemainingBalance ||
                    !confirmDepositMethod))
              "
            >
              <span v-if="confirmBusy">{{ confirmBusyLabel }}</span>
              <span v-else>{{ confirmLabel }}</span>
            </button>
            <button
              v-if="pendingChange?.newValue === 'Consegna Parziale'"
              class="primary-btn confirm-btn"
              @click="handleConfirmOK(true)"
              :disabled="
                confirmBusy ||
                Boolean(confirmBloccato) ||
                (confirmRequiresDeposit &&
                  ((Number(confirmDepositAmount) || 0) >
                    pendingQuoteRemainingBalance ||
                    !confirmDepositMethod))
              "
            >
              <span v-if="confirmBusy">{{ confirmBusyLabel }}</span>
              <span v-else
                ><i class="fa-solid fa-file-arrow-down"></i> Avvia e genera
                DDT</span
              >
            </button>
          </div>
        </template>
      </VueFinalModal>

      <!-- MODALE INSERIMENTO LISTINO O ACCONTO -->
      <VueFinalModal
        v-model="showDiscountModal"
        :clickToClose="true"
        :escToClose="true"
        classes="documents-modal"
        content-class="documents-modal-content modifica-prezzo-modal-content"
      >
        <template #default>
          <div class="modal__header">
            <h3 class="modal__title">Preventivo #{{ currentQuoteId }}</h3>
            <button
              class="modal__close icons-button"
              @click="closeDiscountModal"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <transition-group name="card" tag="div" class="documents-grid">
            <div
              v-for="(quote, i) in allQuotes.filter(
                (q) => q.id === currentQuoteId
              )"
              :key="safeDocKey(quote, i)"
              class="quote-modifier"
            >
              <!-- Modificatori già presenti -->
              <div
                class="current-modifiers"
                v-if="
                  (quote.discount_percent ?? 0) > 0 ||
                  (quote.deposit ?? 0) > 0 ||
                  (quote.surcharge_percent ?? 0) > 0
                "
              >
                <div
                  v-if="(quote.discount_percent ?? 0) > 0"
                  class="modifier-row"
                >
                  <p>
                    <strong>Decremento prezzo applicato: </strong>
                    - {{ Number(quote.discount_percent).toFixed(0) }}%
                    <span v-if="(quote.discount ?? 0) > 0">
                      ({{ formatPriceToPrint(quote.discount) }})
                    </span>
                  </p>
                  <button
                    v-if="canPriceWrite"
                    class="modifier-remove-btn"
                    type="button"
                    title="Rimuovi decremento prezzo"
                    :disabled="removeModifierBusy"
                    @click="removeModifier(quote, 'discount_percent')"
                  >
                    <i class="fa-solid fa-trash-can"></i> Rimuovi
                  </button>
                </div>
                <div
                  v-if="(quote.surcharge_percent ?? 0) > 0"
                  class="modifier-row"
                >
                  <p>
                    <strong>Incremento prezzo applicato: </strong>
                    + {{ Number(quote.surcharge_percent).toFixed(0) }}%
                    <span v-if="(quote.surcharge ?? 0) > 0">
                      ({{ formatPriceToPrint(quote.surcharge) }})
                    </span>
                  </p>
                  <button
                    v-if="canPriceWrite"
                    class="modifier-remove-btn"
                    type="button"
                    title="Rimuovi incremento prezzo"
                    :disabled="removeModifierBusy"
                    @click="removeModifier(quote, 'surcharge_percent')"
                  >
                    <i class="fa-solid fa-trash-can"></i> Rimuovi
                  </button>
                </div>
                <!-- [sc-49299] un acconto per riga, ognuno col suo Rimuovi -->
                <template
                  v-if="versamentiCoerenti(quote.payments, quote.deposit)"
                >
                  <div
                    v-for="p in versamentiCoerenti(
                      quote.payments,
                      quote.deposit
                    )"
                    :key="p.id"
                    class="modifier-row"
                  >
                    <p>
                      <strong>{{ etichettaAcconto(p) }}: </strong>
                      - {{ formatPriceToPrint(p.amount) }}
                    </p>
                    <button
                      v-if="canPriceWrite"
                      class="modifier-remove-btn"
                      type="button"
                      title="Rimuovi questo acconto"
                      :disabled="removeModifierBusy"
                      @click="removePayment(quote, p)"
                    >
                      <i class="fa-solid fa-trash-can"></i> Rimuovi
                    </button>
                  </div>
                </template>
                <!-- acconto senza versamenti (dati incoerenti): riga unica -->
                <div v-else-if="(quote.deposit ?? 0) > 0" class="modifier-row">
                  <p>
                    <strong>Acconto applicato: </strong>
                    - {{ formatPriceToPrint(quote.deposit) }}
                  </p>
                  <button
                    v-if="canPriceWrite"
                    class="modifier-remove-btn"
                    type="button"
                    title="Rimuovi acconto"
                    :disabled="removeModifierBusy"
                    @click="removeModifier(quote, 'deposit')"
                  >
                    <i class="fa-solid fa-trash-can"></i> Rimuovi
                  </button>
                </div>
                <div class="modifier-row modifier-row--residuo">
                  <p>
                    <strong>Residuo da saldare: </strong>
                    {{
                      formatPriceToPrint(
                        Math.max(0, Number(quote.realTotalPrice) || 0)
                      )
                    }}
                  </p>
                </div>
                <p v-if="canPriceWrite" class="hint">
                  Vuoi applicare un'ulteriore modifica al prezzo o acconto?
                </p>
              </div>
              <div v-else class="current-modifiers current-modifiers--empty">
                <p class="modifier-empty">
                  Nessuna modifica di prezzo, scontistica o acconto applicata.
                </p>
              </div>

              <!-- Wizard form applicazione -->
              <div v-if="canPriceWrite" class="wizard-modifier">
                <!-- Step indicator -->
                <div class="wiz-steps">
                  <div
                    class="wiz-step"
                    :class="{ active: wizardStep === 1, done: wizardStep > 1 }"
                  >
                    <div class="wiz-step-dot">
                      <i v-if="wizardStep > 1" class="fa-solid fa-check"></i>
                      <span v-else>1</span>
                    </div>
                    <span class="wiz-step-label">Scelta</span>
                  </div>
                  <div
                    class="wiz-connector"
                    :class="{ done: wizardStep > 1 }"
                  ></div>
                  <div class="wiz-step" :class="{ active: wizardStep === 2 }">
                    <div class="wiz-step-dot"><span>2</span></div>
                    <span class="wiz-step-label">Anteprima</span>
                  </div>
                </div>

                <!-- STEP 1: Scelta tipo + percentuale -->
                <div v-if="wizardStep === 1" class="wiz-panel">
                  <p class="wiz-hint">
                    Scegli la modifica da applicare al preventivo
                  </p>

                  <div class="wiz-type-cards">
                    <button
                      class="wiz-type-card"
                      :class="{
                        selected: modifierType === 'discount_percent',
                        'wiz-type-card--off': modificatoriBloccati.sconto,
                      }"
                      type="button"
                      :disabled="modificatoriBloccati.sconto"
                      :title="
                        modificatoriBloccati.sconto
                          ? modificatoriBloccati.motivoSconto
                          : null
                      "
                      @click="
                        modifierType = 'discount_percent';
                        surchargePercent = 0;
                      "
                    >
                      <span class="wiz-card-icon"
                        ><i class="fa-solid fa-tag"></i
                      ></span>
                      <span class="wiz-badge wiz-badge--cumul"
                        >Decrementa prezzo</span
                      >
                      <span class="wiz-card-desc">Riduzione %</span>
                    </button>
                    <button
                      class="wiz-type-card"
                      :class="{
                        selected: modifierType === 'surcharge_percent',
                        'wiz-type-card--off': modificatoriBloccati.rincaro,
                      }"
                      type="button"
                      :disabled="modificatoriBloccati.rincaro"
                      :title="
                        modificatoriBloccati.rincaro
                          ? modificatoriBloccati.motivoRincaro
                          : null
                      "
                      @click="
                        modifierType = 'surcharge_percent';
                        discountPercent = 0;
                      "
                    >
                      <span class="wiz-card-icon"
                        ><i class="fa-solid fa-arrow-trend-up"></i
                      ></span>
                      <span class="wiz-badge wiz-badge--single"
                        >Incrementa prezzo</span
                      >
                      <span class="wiz-card-desc">Incremento %</span>
                    </button>
                    <button
                      class="wiz-type-card"
                      :class="{ selected: modifierType === 'deposit' }"
                      type="button"
                      @click="
                        modifierType = 'deposit';
                        discountPercent = 0;
                        surchargePercent = 0;
                      "
                    >
                      <span class="wiz-card-icon"
                        ><i class="fa-solid fa-coins"></i
                      ></span>
                      <span class="wiz-badge wiz-badge--deposit">Acconto</span>
                      <span class="wiz-card-desc">Importo €</span>
                    </button>
                  </div>

                  <!-- Incremento e riduzione non convivono: l'API rifiuta con
                       409, qui la card spenta lo dice prima [sc-48768]. -->
                  <p
                    v-if="
                      modificatoriBloccati.sconto ||
                      modificatoriBloccati.rincaro
                    "
                    class="wiz-hint wiz-hint--off"
                  >
                    <i class="fa-solid fa-circle-info"></i>
                    {{
                      modificatoriBloccati.sconto
                        ? modificatoriBloccati.motivoSconto
                        : modificatoriBloccati.motivoRincaro
                    }}
                  </p>

                  <div
                    v-if="
                      modifierType === 'discount_percent' ||
                      modifierType === 'surcharge_percent'
                    "
                    class="wiz-input-row"
                  >
                    <label class="wiz-label">
                      {{
                        modifierType === "discount_percent"
                          ? "Percentuale riduzione"
                          : "Percentuale incremento"
                      }}
                    </label>
                    <div class="wiz-input-wrap">
                      <input
                        v-if="modifierType === 'discount_percent'"
                        type="number"
                        v-model.number="discountPercent"
                        min="0"
                        max="100"
                        step="1"
                        placeholder="0"
                        class="wiz-input"
                      />
                      <input
                        v-else
                        type="number"
                        v-model.number="surchargePercent"
                        min="0"
                        step="1"
                        placeholder="0"
                        class="wiz-input"
                      />
                      <span class="wiz-input-suffix">%</span>
                    </div>
                  </div>

                  <div
                    v-else-if="modifierType === 'deposit'"
                    class="wiz-input-row"
                  >
                    <!-- [sc-49299] importo e metodo affiancati -->
                    <div class="wiz-deposit-campi">
                      <div class="wiz-input-row">
                        <label class="wiz-label">Importo acconto</label>
                        <div class="wiz-input-wrap">
                          <span class="wiz-input-prefix">€</span>
                          <input
                            type="number"
                            v-model.number="depositAmount"
                            min="0"
                            :max="wizardDepositRemaining"
                            step="0.01"
                            placeholder="0.00"
                            class="wiz-input"
                          />
                        </div>
                      </div>
                      <div class="wiz-input-row wiz-deposit-metodo">
                        <label class="wiz-label">Metodo di pagamento</label>
                        <StatusSelect
                          v-model="depositMethod"
                          :options="opzioniMetodoAcconto"
                          placeholder="Scegli il metodo..."
                          placeholderIcon="fa-wallet"
                        />
                      </div>
                    </div>
                    <p
                      v-if="
                        depositAmount > wizardDepositRemaining &&
                        depositAmount > 0
                      "
                      class="wiz-deposit-over-limit"
                    >
                      Supera il residuo da saldare ({{
                        formatPriceToPrint(wizardDepositRemaining)
                      }}).
                    </p>
                  </div>

                  <button
                    class="wiz-btn wiz-btn--primary"
                    type="button"
                    :disabled="!canAdvanceStep1()"
                    @click="wizardStep = 2"
                  >
                    Avanti <i class="fa-solid fa-arrow-right"></i>
                  </button>
                </div>

                <!-- STEP 2: Preview + acconto opzionale -->
                <div v-else-if="wizardStep === 2" class="wiz-panel">
                  <!-- Preview dalla step 1 -->
                  <div class="wiz-preview preview">
                    <div
                      v-if="modifierType === 'discount_percent'"
                      class="breakdown"
                    >
                      <div class="title">
                        Anteprima riduzione
                        <span class="wiz-badge wiz-badge--cumul"
                          >Decrementa prezzo</span
                        >
                      </div>
                      <ul class="calc-list">
                        <li>
                          Totale articoli:
                          <strong>{{
                            formatPriceToPrint(Number(quote.totalPrice) || 0)
                          }}</strong>
                        </li>
                        <li>
                          Riduzione - ({{ anteprimaSconto.percentuale }}%):
                          <strong>{{
                            formatPriceToPrint(anteprimaSconto.importo)
                          }}</strong>
                        </li>
                        <!-- [sc-49299] le spese non si scontano -->
                        <li v-if="anteprimaSconto.spese > 0">
                          Spese aggiuntive:
                          <strong
                            >+
                            {{
                              formatPriceToPrint(anteprimaSconto.spese)
                            }}</strong
                          >
                        </li>
                        <li class="row-highlight">
                          Nuovo totale:
                          <span class="calc-final">
                            <strong>{{
                              formatPriceToPrint(anteprimaSconto.nuovoTotale)
                            }}</strong>
                          </span>
                        </li>
                        <li v-if="anteprimaSconto.acconto > 0">
                          Acconto già versato:
                          <strong
                            >-
                            {{
                              formatPriceToPrint(anteprimaSconto.acconto)
                            }}</strong
                          >
                        </li>
                        <li
                          v-if="anteprimaSconto.acconto > 0"
                          class="row-highlight"
                        >
                          <template v-if="anteprimaSconto.credito > 0">
                            Da restituire al cliente:
                            <span class="calc-final">
                              <strong>{{
                                formatPriceToPrint(anteprimaSconto.credito)
                              }}</strong>
                            </span>
                          </template>
                          <template v-else>
                            Nuovo residuo da saldare:
                            <span class="calc-final">
                              <strong>{{
                                formatPriceToPrint(anteprimaSconto.residuo)
                              }}</strong>
                            </span>
                          </template>
                        </li>
                      </ul>
                    </div>
                    <div
                      v-else-if="modifierType === 'surcharge_percent'"
                      class="breakdown"
                    >
                      <div class="title">
                        Anteprima incremento
                        <span class="wiz-badge wiz-badge--single"
                          >Incrementa prezzo</span
                        >
                      </div>
                      <ul class="calc-list">
                        <li
                          v-if="anteprimaRincaro.precedente > 0"
                          class="calc-replaced"
                        >
                          Incremento attuale + ({{
                            anteprimaRincaro.percentualePrecedente
                          }}%):
                          <strong>{{
                            formatPriceToPrint(anteprimaRincaro.precedente)
                          }}</strong>
                          — verrà sostituito, non sommato
                        </li>
                        <li>
                          Totale articoli:
                          <strong>{{
                            formatPriceToPrint(anteprimaRincaro.base)
                          }}</strong>
                        </li>
                        <li>
                          Incremento + ({{ anteprimaRincaro.percentuale }}%):
                          <strong>{{
                            formatPriceToPrint(anteprimaRincaro.importo)
                          }}</strong>
                        </li>
                        <!-- [sc-49299] le spese non si maggiorano -->
                        <li v-if="anteprimaRincaro.spese > 0">
                          Spese aggiuntive:
                          <strong
                            >+
                            {{
                              formatPriceToPrint(anteprimaRincaro.spese)
                            }}</strong
                          >
                        </li>
                        <li class="row-highlight">
                          Nuovo totale:
                          <span class="calc-final">
                            <strong>{{
                              formatPriceToPrint(anteprimaRincaro.nuovoTotale)
                            }}</strong>
                          </span>
                        </li>
                        <li v-if="anteprimaRincaro.acconto > 0">
                          Acconto già versato:
                          <strong
                            >-
                            {{
                              formatPriceToPrint(anteprimaRincaro.acconto)
                            }}</strong
                          >
                        </li>
                        <li
                          v-if="anteprimaRincaro.acconto > 0"
                          class="row-highlight"
                        >
                          <template v-if="anteprimaRincaro.credito > 0">
                            Da restituire al cliente:
                            <span class="calc-final">
                              <strong>{{
                                formatPriceToPrint(anteprimaRincaro.credito)
                              }}</strong>
                            </span>
                          </template>
                          <template v-else>
                            Nuovo residuo da saldare:
                            <span class="calc-final">
                              <strong>{{
                                formatPriceToPrint(anteprimaRincaro.residuo)
                              }}</strong>
                            </span>
                          </template>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <!-- Preview acconto inserito (solo quando acconto è il modificatore primario) -->
                  <div v-if="modifierType === 'deposit'" class="wiz-optional">
                    <p class="wiz-optional-label">
                      <i class="fa-solid fa-circle-info"></i>
                      Acconto inserito
                    </p>
                    <div
                      v-if="(Number(depositAmount) || 0) > 0"
                      class="preview wiz-deposit-preview"
                    >
                      <div class="breakdown">
                        <div class="title">Con acconto</div>
                        <ul class="calc-list">
                          <li>
                            {{
                              modifierType === "deposit"
                                ? "Importo da saldare:"
                                : "Subtotale dopo modifica:"
                            }}
                            <strong>{{
                              formatPriceToPrint(wizardSubtotal(quote))
                            }}</strong>
                          </li>
                          <li>
                            Acconto:
                            <strong
                              >-
                              {{
                                formatPriceToPrint(Number(depositAmount) || 0)
                              }}</strong
                            >
                          </li>
                          <!-- [sc-49299] come è stato pagato -->
                          <li
                            v-if="modifierType === 'deposit' && depositMethod"
                          >
                            Metodo di pagamento:
                            <strong>{{ depositMethod }}</strong>
                          </li>
                          <li class="row-highlight">
                            Rimanente:
                            <span class="calc-final">
                              <strong>{{
                                formatPriceToPrint(
                                  Math.max(
                                    0,
                                    wizardSubtotal(quote) -
                                      (Number(depositAmount) || 0)
                                  )
                                )
                              }}</strong>
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div class="wiz-actions">
                    <button
                      class="wiz-btn wiz-btn--ghost"
                      type="button"
                      @click="wizardStep = 1"
                    >
                      <i class="fa-solid fa-arrow-left"></i> Indietro
                    </button>
                    <button
                      class="wiz-btn wiz-btn--cta"
                      type="button"
                      :disabled="confirmBusy"
                      @click="applyWizard(quote)"
                    >
                      <span v-if="confirmBusy">Salvo…</span>
                      <span v-else
                        >Applica <i class="fa-solid fa-check"></i
                      ></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- (resto del grid dei documenti, invariato) -->
            <div
              v-for="(doc, i) in filteredDocuments"
              :key="safeDocKey(doc, i)"
              class="document-card"
            >
              <a href="#" class="card-link" @click.prevent="openDoc(doc)">
                <div class="card-icon-pdf">
                  <i class="fa-solid fa-file-pdf"></i>
                </div>
                <div class="card-body">
                  <div class="card-title">{{ formatDocName(doc.name) }}</div>
                  <div class="card-subtitle">
                    Data di creazione: {{ formatToLocalDate(doc.createdAt) }}
                  </div>
                </div>
              </a>
            </div>
            <div v-if="filteredDocuments.length === 0" class="no-result">
              Nessun documento trovato
            </div>
          </transition-group>
        </template>
      </VueFinalModal>
    </div>

    <DdtModal
      :show="showDdtModal"
      :items="ddtModalItems"
      :quote-id="ddtModalQuoteId"
      :client-id="ddtModalClientId"
      :client-name="ddtModalClientName"
      :is-partial-delivery="ddtModalIsPartialDelivery"
      :ddt-consegna-count="ddtModalDdtCount"
      :consegna-complete="ddtModalConsegnaComplete"
      :has-existing-ritiro="ddtModalHasExistingRitiro"
      :has-existing-reso="ddtModalHasExistingReso"
      :deposit-amount="ddtModalDepositAmount"
      :payment-state="ddtModalPaymentState"
      :default-magazziniere-id="ddtModalDefaultMagazziniereId"
      :default-operatore-id="ddtModalDefaultOperatoreId"
      @update:show="showDdtModal = $event"
      @generated="onDdtGenerated"
    />

    <DettaglioModal
      v-model:show="dettaglio.show"
      :titolo="dettaglio.titolo"
      :gruppi="dettaglio.gruppi"
      :loading="dettaglio.loading"
    />

    <!-- [sc-49299] finestra di stampa di «Scarica nuovamente il PDF» -->
    <StampaPreventivoModal
      :show="stampa.show"
      :dati="stampa.dati"
      :salvando="stampa.salvando"
      @scarica="onScaricaStampa"
      @chiudi="onChiudiStampa"
    />
  </main>
  <router-view></router-view>
</template>

<script>
import logoPath from "@/assets/barcio-logo.png";
import axiosInstance from "@/axiosInstance";
import { mapActions, mapGetters } from "vuex/dist/vuex.cjs.js";
import {
  formatPriceToPrint,
  formatToLocalDate,
  formatToLocalDateAndHours,
} from "../utilities/formatting";
import {
  azzeraModificatore,
  daRestituire,
  limiteMerceAcconto,
  pezzoEntroLimiteAcconto,
  ricalcolaRimanente,
  valoreMerceMossa,
} from "@/utilities/pagamenti";
import DdtModal from "@/components/DdtModal.vue";
import StatusSelect from "@/components/StatusSelect.vue";
import DettaglioModal from "@/components/DettaglioModal.vue";
import AzioniRiga from "@/components/AzioniRiga.vue";
import dettaglioRiga from "@/mixins/dettaglioRiga";
import apriDocumento from "@/mixins/apriDocumento";
import stampaPreventivo from "@/mixins/stampaPreventivo";
import StampaPreventivoModal from "@/components/StampaPreventivoModal.vue";
import {
  clientePerPdf,
  datiPdf,
  pdfPreventivo,
} from "@/utilities/pdfPreventivo";
import { tuttiICampi } from "@/utilities/campiStampa";
import { nomeDaUrl } from "@/utilities/openDocument";
import { dettaglioPreventivo } from "@/utilities/dettaglio";
import { totaleSpese } from "@/utilities/spese";
import {
  METODI_ACCONTO,
  etichettaAcconto,
  metodoIniziale,
  versamentiCoerenti,
} from "@/utilities/acconti";

const CONFERMA_OPTIONS = [
  { value: false, label: "In attesa", color: "#ffa726" },
  { value: true, label: "Confermato", color: "#4caf50" },
];

const PAGAMENTO_OPTIONS = [
  { value: "In attesa", label: "In attesa", color: "#ffa726" },
  {
    value: "Pagato con Acconto",
    label: "Pagato con Acconto",
    color: "#2196f3",
  },
  { value: "Pagato", label: "Pagato", color: "#4caf50" },
];

const SPEDIZIONE_COLOR = {
  "In magazzino": "#9ca3af",
  "Consegna Parziale": "#9c27b0",
  "In consegna": "#ffa726",
  Consegnato: "#4caf50",
};

export default {
  components: {
    DdtModal,
    StatusSelect,
    DettaglioModal,
    AzioniRiga,
    StampaPreventivoModal,
  },
  mixins: [dettaglioRiga, apriDocumento, stampaPreventivo],

  computed: {
    ...mapGetters({
      allQuotes: "quotes/list",
      pagination: "quotes/pagination",
    }),

    canPriceWrite() {
      const role = this.$store.state.currentUser?.role;
      return role === "responsabile" || role === "direzione";
    },

    visiblePages() {
      const range = 2;
      const start = Math.max(1, this.pagination.currentPage - range);
      const end = Math.min(
        this.pagination.totalPages,
        this.pagination.currentPage + range
      );
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    filteredDocuments() {
      const list = Array.isArray(this.selectedDocuments)
        ? this.selectedDocuments
        : [];
      const term = (this.docSearchTerm || "").toLowerCase().trim();
      const seen = new Set();
      return list.filter(Boolean).filter((doc) => {
        const k = doc.id ?? doc.name ?? doc.url ?? Math.random();
        if (seen.has(k)) return false;
        seen.add(k);
        return term ? (doc.name || "").toLowerCase().includes(term) : true;
      });
    },
    pendingQuoteRemainingBalance() {
      if (!this.pendingChange?.id) return 0;
      const q = this.allQuotes.find((x) => x.id === this.pendingChange.id);
      return Math.max(0, Number(q?.realTotalPrice) || 0);
    },
    // Incremento e riduzione non convivono sullo stesso preventivo: la regola
    // sta nell'API (`applyDealModifier`, 409) e qui spegne la card [sc-48768].
    modificatoriBloccati() {
      const q = this.allQuotes.find((x) => x.id === this.currentQuoteId) || {};
      const rincaro = Number(q.surcharge) || 0;
      const sconto = Number(q.discount) || 0;
      return {
        sconto: rincaro > 0,
        rincaro: sconto > 0,
        motivoSconto:
          "Sul preventivo c'è già un incremento di prezzo: toglilo prima di applicare una riduzione.",
        motivoRincaro:
          "Sul preventivo c'è già una riduzione di prezzo: toglila prima di applicare un incremento.",
      };
    },

    // Anteprima della riduzione [sc-48789]: la percentuale si applica al
    // totale articoli, l'acconto si sottrae dopo.
    anteprimaSconto() {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      const q = this.allQuotes.find((x) => x.id === this.currentQuoteId) || {};
      const totale = round2(Number(q.totalPrice) || 0);
      const acconto = round2(Number(q.deposit) || 0);
      const percentuale = Number(this.discountPercent) || 0;
      const importo = round2((totale * percentuale) / 100);
      // [sc-49299] spese fuori dalla percentuale, ma nel totale
      const spese = totaleSpese(q);
      const nuovoTotale = round2(Math.max(0, round2(totale - importo)) + spese);
      const residuo = round2(nuovoTotale - acconto);
      return {
        totale,
        spese,
        acconto,
        percentuale: percentuale.toFixed(0),
        importo,
        nuovoTotale,
        residuo: Math.max(0, residuo),
        credito: daRestituire(residuo),
      };
    },

    // Anteprima del rincaro in percentuale [sc-48768]: la percentuale si applica
    // al totale articoli e l'API SOSTITUISCE il rincaro precedente, non lo somma.
    anteprimaRincaro() {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      const q = this.allQuotes.find((x) => x.id === this.currentQuoteId) || {};
      const base = round2(Number(q.totalPrice) || 0);
      const percentuale = Number(this.surchargePercent) || 0;
      const importo = round2((base * percentuale) / 100);
      // [sc-49299] spese fuori dalla percentuale, ma nel totale
      const spese = totaleSpese(q);
      const nuovoTotale = round2(base + importo + spese);
      const acconto = round2(Number(q.deposit) || 0);
      const residuo = round2(nuovoTotale - acconto - (Number(q.discount) || 0));
      return {
        base,
        spese,
        percentuale: percentuale.toFixed(0),
        importo,
        nuovoTotale,
        acconto,
        residuo: Math.max(0, residuo),
        credito: daRestituire(residuo),
        precedente: round2(Number(q.surcharge) || 0),
        percentualePrecedente: Number(q.surcharge_percent) || 0,
      };
    },
    wizardDepositRemaining() {
      const q = this.allQuotes.find((x) => x.id === this.currentQuoteId);
      return Math.max(0, Number(q?.realTotalPrice) || 0);
    },
    ddtDocumentsByCausale() {
      const groups = { consegna: [], reso: [], ritiro: [] };
      for (const doc of this.selectedDdtDocuments) {
        const key = doc._causale in groups ? doc._causale : "consegna";
        groups[key].push(doc);
      }
      const byDate = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
      return {
        consegna: [...groups.consegna].sort(byDate),
        reso: [...groups.reso].sort(byDate),
        ritiro: [...groups.ritiro].sort(byDate),
      };
    },
  },

  data() {
    return {
      tooltipOpenId: null,
      hoveredId: null,
      hideTimer: null,
      lockClickId: null, // anti-ghost click

      isLoading: false,
      CONFERMA_OPTIONS,
      PAGAMENTO_OPTIONS,

      refreshing: false,
      refreshed: false,
      errorMessage: "",
      successMessage: "",
      currentPage: 1,
      searchTerm: "",
      currentQuoteId: null,
      showDocumentsModal: false,
      rigenerandoPdf: false,
      docModalSection: "pdf",
      selectedDocuments: [],
      selectedDdtDocuments: [],
      showArchived: false,
      hasArchived: false,
      filterConfirmed: undefined,
      depositAmount: 0,
      wizardStep: 1,
      modifierType: "",
      discountPercent: 0,
      surchargePercent: 0,
      docSearchTerm: "", // per ricerca documenti nella modale

      /* Modali archiviazione / ripristino */
      showArchiveModal: false,
      archiving: false,
      showRestoreModal: false,
      restoring: false,
      selectedQuote: null,

      showConfirmModal: false,
      showDiscountModal: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmLabel: "Conferma",
      confirmBusyLabel: "Aggiorno…",
      confirmBusy: false,
      confirmRequiresDeposit: false,
      confirmDepositAmount: "",
      // [sc-49299] metodo degli acconti (conferma «Pagato con Acconto» e wizard)
      confirmDepositMethod: "",
      depositMethod: "",
      opzioniMetodoAcconto: METODI_ACCONTO.map((m) => ({ value: m, label: m })),
      confirmBloccato: "", // motivo per cui la conferma non può partire
      removeModifierBusy: false,
      pendingChange: null, // { id, field, newValue }

      showDdtModal: false,
      ddtModalItems: [],
      ddtModalQuoteId: null,
      ddtModalClientId: null,
      ddtModalClientName: null,
      ddtModalIsPartialDelivery: false,
      ddtModalDdtCount: 0,
      ddtModalConsegnaComplete: false,
      ddtModalHasExistingRitiro: false,
      ddtModalHasExistingReso: false,
      ddtModalDepositAmount: 0,
      ddtModalPaymentState: null,
      ddtModalDefaultMagazziniereId: null,
      ddtModalDefaultOperatoreId: null,
    };
  },

  methods: {
    // [sc-49299] acconti: righe della finestra delle modifiche
    versamentiCoerenti,
    etichettaAcconto,
    apriDettaglioPreventivo(event, quote) {
      const cliente = quote.Client?.clientName;
      this.apriDettaglio(
        event,
        `Preventivo #${quote.id}${cliente ? ` — ${cliente}` : ""}`,
        () => dettaglioPreventivo(quote)
      );
    },

    // [sc-48893] le tre select di stato: stesse regole nelle colonne larghe
    // e nella colonna «Stato» impilata
    campiStato(quote) {
      return [
        {
          key: "isConfirmed",
          label: "Conferma",
          options: CONFERMA_OPTIONS,
          disabled: Boolean(quote.isConfirmed),
        },
        {
          key: "payment_state",
          label: "Pagamento",
          options:
            quote.payment_state === "Pagato con Acconto"
              ? PAGAMENTO_OPTIONS.filter((o) => o.value !== "In attesa")
              : PAGAMENTO_OPTIONS,
          disabled: quote.payment_state === "Pagato" || !quote.isConfirmed,
        },
        {
          key: "state",
          label: "Spedizione",
          options: this.spedizioneOptions(quote),
          disabled: this.isStateSelectDisabled(quote),
        },
      ];
    },
    cambiaStato(quote, key, val) {
      quote[key] = val;
      this.onQuoteStateChange(quote, key, val);
    },
    // "3/10 pz consegnati" durante una consegna parziale, altrimenti ""
    consegnaParziale(quote) {
      if (
        quote.state !== "Consegna Parziale" ||
        !(quote.ddtConsegnaCount > 0) ||
        !(quote.quoteTotalQty > 0)
      ) {
        return "";
      }
      return `${Math.floor(Number(quote.ddtDeliveredQty))}/${Math.floor(
        Number(quote.quoteTotalQty)
      )} pz consegnati`;
    },
    spedizioneOptions(quote) {
      return this.availableStateOptions(quote).map((opt) => ({
        ...opt,
        color: SPEDIZIONE_COLOR[opt.value] ?? "#9ca3af",
      }));
    },

    formatAddress(address) {
      if (!address) return "Indirizzo non disponibile";
      const withCap = address.replace(/,?\s+(\d{5}\s)/, "\n$1");
      if (withCap !== address) return withCap;
      return address.replace(/,\s*/, ",\n");
    },

    async handleRefresh() {
      this.refreshing = true;
      this.refreshed = false;
      try {
        await this.getQuotes(this.pagination.currentPage);
      } finally {
        this.refreshing = false;
        this.refreshed = true;
        setTimeout(() => {
          this.refreshed = false;
        }, 1500);
      }
    },

    availableStateOptions(quote) {
      const s = quote.state;
      if (s === "In magazzino") {
        if (quote.payment_state === "Pagato con Acconto") {
          return [
            { value: "In magazzino", label: "In magazzino", disabled: true },
            {
              value: "Consegna Parziale",
              label: "Consegna Parziale",
              disabled: false,
            },
          ];
        }
        return [
          { value: "In magazzino", label: "In magazzino", disabled: true },
          { value: "In consegna", label: "In consegna", disabled: false },
        ];
      }
      if (s === "Consegna Parziale") {
        return [
          {
            value: "Consegna Parziale",
            label: "Consegna Parziale",
            disabled: true,
          },
        ];
      }
      if (s === "In consegna") {
        return [
          { value: "In consegna", label: "In consegna", disabled: true },
          { value: "Consegnato", label: "Consegnato", disabled: false },
        ];
      }
      return [
        { value: s || "Consegnato", label: s || "Consegnato", disabled: true },
      ];
    },

    isStateSelectDisabled(quote) {
      if (quote.state === "Consegnato") return true;
      if (quote.state === "Consegna Parziale") return true;
      if (quote.state === "In magazzino") {
        return (
          !quote.isConfirmed ||
          !["Pagato", "Pagato con Acconto"].includes(quote.payment_state)
        );
      }
      return !quote.isConfirmed;
    },

    canApplyModifier() {
      if (this.modifierType === "discount_percent") {
        return this.discountPercent > 0 && this.discountPercent <= 100;
      }
      if (this.modifierType === "surcharge_percent") {
        return this.surchargePercent > 0;
      }
      if (this.modifierType === "deposit") {
        return this.depositAmount > 0 && Boolean(this.depositMethod);
      }
      return false;
    },
    canAdvanceStep1() {
      if (this.modifierType === "discount_percent") {
        return this.discountPercent > 0 && this.discountPercent <= 100;
      }
      if (this.modifierType === "surcharge_percent") {
        return this.surchargePercent > 0;
      }
      if (this.modifierType === "deposit") {
        return (
          this.depositAmount > 0 &&
          this.depositAmount <= this.wizardDepositRemaining &&
          Boolean(this.depositMethod) // [sc-49299]
        );
      }
      return false;
    },
    wizardSubtotal(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      if (this.modifierType === "discount_percent") {
        // [sc-48789] la riduzione si calcola sul totale articoli, come la
        // maggiorazione: l'acconto si sottrae dopo, non prima.
        // [sc-49299] le spese si aggiungono dopo, senza percentuale
        const base = Number(quote.totalPrice) || 0;
        return round2(
          Math.max(0, round2(base * (1 - Number(this.discountPercent) / 100))) +
            totaleSpese(quote)
        );
      }
      if (this.modifierType === "surcharge_percent") {
        const base = Number(quote.totalPrice) || 0;
        return round2(
          base * (1 + Number(this.surchargePercent) / 100) + totaleSpese(quote)
        );
      }
      if (this.modifierType === "deposit") {
        return Math.max(
          0,
          round2(Number(quote.realTotalPrice) || Number(quote.totalPrice) || 0)
        );
      }
      return Number(quote.totalPrice) || 0;
    },
    async applyWizard(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      this.confirmBusy = true;
      try {
        if (this.modifierType === "discount_percent") {
          const base = Number(quote.totalPrice) || 0; // [sc-48789]
          const percent = Number(this.discountPercent || 0);
          const amount = round2(base * (percent / 100));
          await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
            type: "discount_percent",
            percent,
            amount,
          });
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.discount_percent = percent;
            q.discount = amount;
            q.realTotalPrice = ricalcolaRimanente(q);
          }
        } else if (this.modifierType === "surcharge_percent") {
          const percent = Number(this.surchargePercent || 0);
          const base = Number(quote.totalPrice) || 0;
          const amount = round2(base * (percent / 100));
          await axiosInstance.post(`quote/priceAdjustment/${quote.id}`, {
            type: "surcharge_percent",
            percent,
            amount,
          });
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.surcharge_percent = percent;
            q.surcharge = amount;
            q.realTotalPrice = ricalcolaRimanente(q);
          }
        }

        if ((Number(this.depositAmount) || 0) > 0) {
          const amount = round2(Number(this.depositAmount));
          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "deposit", amount, method: this.depositMethod }
          );
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.deposit = round2((Number(q.deposit) || 0) + amount);
            q.realTotalPrice = ricalcolaRimanente(q);
            // [sc-49299] il PDF generato subito dopo stampa anche questo
            if (data?.payment)
              q.payments = [...(q.payments || []), data.payment];
          }
        }

        this.$toast.success("Preventivo aggiornato.");

        const qLocal = this.allQuotes.find((x) => x.id === quote.id);
        const needsFetch = !qLocal?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch
          ? await this.getQuotebyId(quote.id)
          : qLocal;
        await this.generatePdf(dataForPdf);
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nel salvataggio."
        );
      } finally {
        this.confirmBusy = false;
      }
    },
    // [sc-49299] toglie un solo acconto: il suo importo esce dal totale versato
    async removePayment(quote, payment) {
      this.removeModifierBusy = true;
      try {
        const { data } = await axiosInstance.delete(
          `quote/${quote.id}/payments/${payment.id}`
        );
        const q = this.allQuotes.find((x) => x.id === quote.id);
        if (q) {
          q.payments = (q.payments || []).filter((p) => p.id !== payment.id);
          q.deposit = Number(data?.deposit) || 0;
          q.realTotalPrice = ricalcolaRimanente(q);
        }
        this.$toast.success("Acconto rimosso.");
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella rimozione."
        );
      } finally {
        this.removeModifierBusy = false;
      }
    },
    async removeModifier(quote, type) {
      this.removeModifierBusy = true;
      try {
        await axiosInstance.delete(`quote/priceAdjustment/${quote.id}`, {
          data: { type },
        });
        // [sc-48852] azzera il modificatore e ricalcola il rimanente in lista
        azzeraModificatore(
          this.allQuotes.find((x) => x.id === quote.id),
          type
        );
        // [sc-49299] l'acconto intero porta via anche i versamenti
        if (type === "deposit") {
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) q.payments = [];
        }
        const successMsg =
          type === "discount_percent"
            ? "Decremento prezzo rimosso."
            : type === "surcharge_percent"
            ? "Incremento prezzo rimosso."
            : "Acconto rimosso.";
        this.$toast.success(successMsg);
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nella rimozione."
        );
      } finally {
        this.removeModifierBusy = false;
      }
    },

    // [sc-49300] articoli e prezzi unitari, dalle stesse fonti delle righe
    // della finestra DDT (quote.items, poi quote_items non già visti)
    righePrezzi(quote) {
      const visti = new Set((quote.items || []).map((it) => it.id));
      return [
        ...(quote.items || []).map((it) => ({
          itemId: it.id || null,
          price: Number(it?.QuoteItem?.price || 0),
        })),
        ...(quote.quote_items || [])
          .filter((qi) => !(qi?.item?.id && visti.has(qi.item.id)))
          .map((qi) => ({
            itemId: qi?.item?.id || null,
            price: Number(qi.price || 0),
          })),
      ];
    },

    // [sc-49300] la consegna parziale può partire solo se almeno un pezzo
    // sta nel limite dell'acconto, tolta la merce già movimentata (ritiri)
    async controllaLimiteConsegna(quote) {
      const righe = this.righePrezzi(quote);
      let giaMosso = 0;
      try {
        const { data: mossi } = await axiosInstance.get(
          `/ddt/quote/${quote.id}/moved-qty`
        );
        const perArticolo = Object.fromEntries(
          mossi.map((r) => [r.itemId, Number(r.totalMoved)])
        );
        giaMosso = valoreMerceMossa(
          righe.map((r) => ({
            ...r,
            movedQty: r.itemId != null ? perArticolo[r.itemId] || 0 : 0,
          }))
        );
      } catch {
        // non bloccante: la finestra DDT controlla comunque il limite
      }
      const prezzi = righe.map((r) => r.price);
      if (pezzoEntroLimiteAcconto(prezzi, quote.deposit, giaMosso)) return;
      const limite = limiteMerceAcconto(quote.deposit);
      const motivo =
        giaMosso > 0
          ? `del limite di ${formatPriceToPrint(limite)} restano ` +
            `${formatPriceToPrint(Math.max(0, limite - giaMosso))} ` +
            `(merce già movimentata: ${formatPriceToPrint(giaMosso)}), ` +
            `meno dell'articolo più economico`
          : `l'articolo più economico supera il limite di ${formatPriceToPrint(
              limite
            )}`;
      this.confirmBloccato =
        `Con questo acconto non si può consegnare nemmeno un pezzo: ` +
        `${motivo}. Aumenta l'acconto o segna il preventivo come Pagato.`;
    },

    async openDdtModal(quote) {
      const AREA_CATS = [
        "piastrelle",
        "rivestimenti parete",
        "parquet",
        "laminati",
        "decking esterno",
      ];
      const LINEAR_CATS = ["profili", "sistemi di scarico"];
      const rows = [];
      const seen = new Set();

      (quote.items || []).forEach((it) => {
        seen.add(it.id);
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const qty = it?.QuoteItem?.quantity ?? 0;
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          itemId: it.id || null,
          description: it.itemName || it.itemCode || "—",
          quantity: qty,
          uom,
          price: Number(it?.QuoteItem?.price || 0),
        });
      });

      (quote.quote_items || []).forEach((qi) => {
        if (qi?.item?.id && seen.has(qi.item.id)) return;
        const it = qi.item || {};
        const isArea = AREA_CATS.includes((it.category || "").toLowerCase());
        const isLinear = LINEAR_CATS.includes(
          (it.category || "").toLowerCase()
        );
        const uom = it.salesUnit || (isArea ? "SC" : isLinear ? "CONF" : "PZ");
        rows.push({
          itemId: it.id || null,
          description: it.itemName || it.itemCode || "—",
          quantity: qi.quantity ?? 0,
          uom,
          price: Number(qi.price || 0),
        });
      });

      const isPartial = quote.state === "Consegna Parziale";
      const ddtCount = quote.ddtConsegnaCount || 0;

      if (quote.state === "Consegnato") {
        rows.forEach((row) => {
          row.originalQty = row.quantity;
          row.deliveredQty = row.quantity;
        });
      } else if (isPartial && ddtCount > 0) {
        try {
          const { data: deliveredQtys } = await axiosInstance.get(
            `/ddt/quote/${quote.id}/delivered-qty`
          );
          const deliveredMap = Object.fromEntries(
            deliveredQtys.map((r) => [r.itemId, Number(r.totalDelivered)])
          );
          rows.forEach((row) => {
            if (row.itemId != null) {
              row.originalQty = row.quantity;
              row.deliveredQty = deliveredMap[row.itemId] || 0;
              row.quantity = Math.max(0, row.quantity - row.deliveredQty);
            } else {
              row.deliveredQty = 0;
            }
          });
        } catch {
          rows.forEach((row) => {
            row.deliveredQty = 0;
          });
        }
      } else {
        rows.forEach((row) => {
          row.deliveredQty = 0;
        });
      }

      try {
        const { data: movedQtys } = await axiosInstance.get(
          `/ddt/quote/${quote.id}/moved-qty`
        );
        const movedMap = Object.fromEntries(
          movedQtys.map((r) => [r.itemId, Number(r.totalMoved)])
        );
        rows.forEach((row) => {
          row.movedQty = row.itemId != null ? movedMap[row.itemId] || 0 : 0;
        });
      } catch {
        rows.forEach((row) => {
          row.movedQty = 0;
        });
      }

      const deliveredTotal = Number(quote.ddtDeliveredQty) || 0;
      const quoteTotal = Number(quote.quoteTotalQty) || 0;
      const consegnaComplete =
        quote.state === "Consegnato" ||
        (isPartial &&
          ddtCount > 0 &&
          quoteTotal > 0 &&
          deliveredTotal >= quoteTotal);

      let hasExistingRitiro = false;
      let hasExistingReso = false;
      let lastConsegnaMagazziniereId = null;
      let lastConsegnaOperatoreId = null;
      try {
        const { data: ddts } = await axiosInstance.get("/ddt", {
          params: { quoteId: quote.id },
        });
        hasExistingRitiro = ddts.some((d) => d.causale === "ritiro");
        hasExistingReso = ddts.some((d) => d.causale === "reso");
        const lastConsegna = ddts.find((d) => d.causale === "consegna");
        if (lastConsegna) {
          lastConsegnaMagazziniereId = lastConsegna.magazziniereId || null;
          lastConsegnaOperatoreId = lastConsegna.operatoreId || null;
        }
      } catch {
        // non bloccante
      }

      this.ddtModalItems = rows;
      this.ddtModalQuoteId = quote.id;
      this.ddtModalClientId = quote.clientId || null;
      this.ddtModalClientName = quote?.Client?.clientName || null;
      this.ddtModalIsPartialDelivery = isPartial;
      this.ddtModalDdtCount = ddtCount;
      this.ddtModalConsegnaComplete = consegnaComplete;
      this.ddtModalHasExistingRitiro = hasExistingRitiro;
      this.ddtModalHasExistingReso = hasExistingReso;
      this.ddtModalDepositAmount = Number(quote.deposit) || 0;
      this.ddtModalPaymentState = quote.payment_state || null;
      this.ddtModalDefaultMagazziniereId = lastConsegnaMagazziniereId;
      this.ddtModalDefaultOperatoreId = lastConsegnaOperatoreId;
      this.showDdtModal = true;
    },

    async onDdtGenerated({ ddt, isLastDelivery }) {
      if (ddt?.causale === "consegna" && this.ddtModalQuoteId) {
        const q = this.allQuotes.find((x) => x.id === this.ddtModalQuoteId);
        if (q) {
          q.ddtConsegnaCount = (q.ddtConsegnaCount || 0) + 1;
          const deliveredInThisDdt = (ddt.ddt_items || []).reduce(
            (sum, it) => sum + Number(it.quantity || 0),
            0
          );
          q.ddtDeliveredQty =
            (Number(q.ddtDeliveredQty) || 0) + deliveredInThisDdt;
        }
      }
      if (isLastDelivery && this.ddtModalQuoteId) {
        await this.updateQuoteState(this.ddtModalQuoteId, "Consegnato");
      }
    },

    ...mapActions([
      "fetchQuotes",
      "archiveQuote",
      "restoreQuote",
      "hasArchivedQuotes",
      "uploadQuoteDocument",
    ]),

    isTooltipVisible(id) {
      return this.tooltipOpenId === id || this.hoveredId === id;
    },
    applyTooltipPlacement(id) {
      const tip = document.getElementById("tt-" + id);
      if (tip) tip.dataset.placement = "over";
    },
    onCellEnter(id) {
      clearTimeout(this.hideTimer);
      this.hoveredId = id;
      this.$nextTick(() => this.applyTooltipPlacement(id));
    },
    onCellLeave(id) {
      clearTimeout(this.hideTimer);
      // piccolo delay per evitare micro-sfarfallii
      this.hideTimer = setTimeout(() => {
        // se non è aperto "sticky" con click, chiudi l'hover
        if (this.tooltipOpenId !== id) this.hoveredId = null;
      }, 120);
    },
    toggleTooltip(id) {
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id; // blocca click subito dopo l’apertura
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250); // ~250ms bastano per il ghost click
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },

    onTriggerTouch(id) {
      // equivalente touch del click, con lock sicuro
      this.tooltipOpenId = this.tooltipOpenId === id ? null : id;
      if (this.tooltipOpenId === id) {
        this.hoveredId = id;
        this.lockClickId = id;
        setTimeout(() => {
          if (this.lockClickId === id) this.lockClickId = null;
        }, 250);
        this.$nextTick(() => this.applyTooltipPlacement(id));
      }
    },
    onTriggerKeydown(e, id) {
      if (e.key === "Escape") {
        this.tooltipOpenId = null;
        this.hoveredId = null;
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleTooltip(id);
      }
    },
    // se vuoi mantenere queste API:
    showTooltip(id) {
      this.onCellEnter(id);
    },
    hideTooltip() {
      // opzionale: se chiamata da blur del bottone
      const id = this.tooltipOpenId ?? this.hoveredId;
      this.onCellLeave(id);
    },
    handleDocumentClick(e) {
      // chiudi se clicco fuori da qualsiasi .client-cell
      if (!e.target.closest(".client-cell")) {
        this.tooltipOpenId = null;
      }
    },
    async copyToClipboard(text, kind = null) {
      const value = String(text ?? "").trim();

      // Se il tipo non è passato, prova a inferirlo
      if (!kind) {
        if (/^\+?[\d()\s.-]{5,}$/.test(value)) kind = "phone";
        else if (/@/.test(value)) kind = "email"; // PEC se vuoi, vedi sotto
        else kind = "text";
      }

      try {
        await navigator.clipboard.writeText(value);

        const msgMap = {
          phone: "Numero di telefono copiato",
          email: "Email copiata",
          pec: "PEC copiata",
          text: "Testo copiato",
        };

        this.$toast?.success?.(`${msgMap[kind] || msgMap.text} negli appunti`);
      } catch {
        this.$toast?.error?.("Impossibile copiare negli appunti");
      }
    },

    safeDocKey(doc, i) {
      if (doc && doc.id != null) return "doc-" + String(doc.id);
      if (doc && doc.url) return "url-" + doc.url;
      if (doc && doc.name) return "name-" + doc.name;
      return "idx-" + String(i);
    },

    formatToLocalDate,
    formatToLocalDateAndHours,
    formatPriceToPrint,
    daRestituire,

    formatDocName(name) {
      return (
        (name ?? "")
          .toString()
          // prima togli eventuale ".dev" immediatamente prima di ".pdf"
          .replace(/\.dev(?=\.pdf$)/i, "")
          // poi rimuovi "_N_<numero>.pdf" in fondo
          .replace(/_N_\d+\.pdf$/i, "")
      );
    },

    async openDocumentsModal(quote, section = "pdf") {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.docModalSection = section;
      this.selectedDocuments = [];
      this.docSearchTerm = "";
      this.showDocumentsModal = true;
      const quoteDocs = this.documentiPreventivo(quote);
      let ddtDocs = [];
      if (quote.id) {
        try {
          const { data } = await axiosInstance.get("/ddt", {
            params: { quoteId: quote.id },
          });
          ddtDocs = (data || [])
            .filter((d) => d.documentUrl)
            .map((d) => ({
              id: `ddt-${d.id}`,
              name: `DDT_N_${d.id}.pdf`,
              url: d.documentUrl,
              objectName: nomeDaUrl(d.documentUrl),
              createdAt: d.date ?? d.createdAt,
              _type: "ddt",
              _causale: d.causale,
              _ddtId: d.id,
            }));
        } catch {
          /* ignora errori fetch DDT */
        }
      }
      this.selectedDocuments = quoteDocs;
      this.selectedDdtDocuments = ddtDocs;
    },
    documentiPreventivo(quote) {
      return (quote?.documents || []).filter(Boolean).map((d) => ({
        ...d,
        _type: "quote",
        _quoteUpdatedAt: quote.updatedAt ?? null,
      }));
    },
    // [sc-48860] rigenera il PDF dai dati attuali: sostituisce quello caricato
    // (stesso nome) e lo scarica. [sc-49299] Passa dalla finestra di stampa;
    // dopo lo scarico si aggiorna l'elenco nella modale (dopoStampa).
    async rigeneraPdf() {
      const id = this.selectedQuote?.id;
      if (!id || this.rigenerandoPdf) return;
      this.rigenerandoPdf = true;
      try {
        const qLocal =
          this.allQuotes.find((x) => x.id === id) || this.selectedQuote;
        const needsFetch = !qLocal?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch ? await this.getQuotebyId(id) : qLocal;
        this.apriStampa(this.datiPdfPreventivo(dataForPdf));
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.details ||
            error?.response?.data?.error ||
            "Errore durante la preparazione del PDF."
        );
      } finally {
        this.rigenerandoPdf = false;
      }
    },
    async dopoStampa(nome, id) {
      await this.getQuotes(this.pagination.currentPage);
      const aggiornato = this.allQuotes.find((x) => x.id === id);
      if (aggiornato && this.selectedQuote?.id === id) {
        this.selectedQuote = aggiornato;
        this.selectedDocuments = this.documentiPreventivo(aggiornato);
      }
    },
    closeDocumentsModal() {
      this.showDocumentsModal = false;
      this.docModalSection = "pdf";
      this.selectedDocuments = [];
      this.selectedDdtDocuments = [];
      this.currentQuoteId = null;
      this.selectedQuote = null;
      this.docSearchTerm = "";
    },

    /* ----- ARCHIVIAZIONE / RIPRISTINO con modale ----- */
    openArchiveModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showArchiveModal = true;
    },
    closeArchiveModal() {
      if (this.archiving) return;
      this.showArchiveModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmArchive() {
      if (!this.selectedQuote) return;
      try {
        this.archiving = true;
        let response;
        if (this.$store._actions?.archiveQuote) {
          response = await this.archiveQuote(this.selectedQuote.id);
        } else {
          response = await this.$axios.patch(
            `quote/archive/${this.selectedQuote.id}`,
            JSON.stringify({ archived: true })
          );
        }
        this.successMessage =
          response.data?.message || "Preventivo archiviato con successo.";
        this.$toast.success(this.successMessage);

        this.showArchiveModal = false;
        this.currentQuoteId = null;

        await this.refreshArchivedFlag();
        // await this.fetchQuotes({
        //   page: this.pagination.currentPage,
        //   limit: 4,
        //   search: this.searchTerm,
        //   archived: this.showArchived,
        // });
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.errorMessage =
          err.response?.data?.details ||
          "Impossibile archiviare il preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.archiving = false;
        this.selectedQuote = null;
      }
    },

    openRestoreModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.showRestoreModal = true;
    },
    openDiscountModal(quote) {
      this.currentQuoteId = quote?.id ?? null;
      this.selectedQuote = quote;
      this.depositMethod = metodoIniziale(quote); // [sc-49299]
      this.showDiscountModal = true;
      this.docSearchTerm = ""; // reset ricerca
      this.selectedDocuments = quote.documents || [];
    },
    closeDiscountModal() {
      if (this.restoring) return;
      this.showDiscountModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
      this.wizardStep = 1;
      this.modifierType = "";
      this.discountPercent = 0;
      this.surchargePercent = 0;
      this.depositAmount = 0;
      this.depositMethod = "";
      this.selectedDocuments = [];
      this.docSearchTerm = "";
    },
    closeRestoreModal() {
      if (this.restoring) return;
      this.showRestoreModal = false;
      this.selectedQuote = null;
      this.currentQuoteId = null;
    },
    async confirmRestore() {
      if (!this.selectedQuote) return;
      try {
        this.restoring = true;
        let response;
        if (this.$store._actions?.restoreQuote) {
          response = await this.restoreQuote(this.selectedQuote.id);
        } else {
          response = await this.$axios.patch(
            `quote/archive/${this.selectedQuote.id}`,
            JSON.stringify({ isArchived: false })
          );
        }
        this.successMessage =
          response.data?.message || "Preventivo ripristinato con successo.";
        this.$toast.success(this.successMessage);

        this.showRestoreModal = false;
        this.currentQuoteId = null;

        await this.refreshArchivedFlag();

        if (this.showArchived && !this.hasArchived) {
          this.showArchived = false;
          await this.getQuotes(1); // carica la prima pagina degli attivi
          return; // evita ulteriori fetch sulla vista archiviati
        }
        // await this.fetchQuotes({
        //   page: this.pagination.currentPage,
        //   limit: 4,
        //   search: this.searchTerm,
        //   archived: this.showArchived,
        //   isConfirmed: this.filterConfirmed,
        // });
        await this.getQuotes(this.pagination.currentPage);
      } catch (err) {
        this.errorMessage =
          err.response?.data?.message ||
          err.response?.data?.details ||
          "Impossibile ripristinare il preventivo.";
        this.$toast.error(this.errorMessage);
      } finally {
        this.restoring = false;
        this.selectedQuote = null;
      }
    },

    /* ----- Ricerca/paginazione ----- */
    async onSearch() {
      this.isLoading = true;
      try {
        this.currentPage = 1;
        await this.fetchQuotes({
          page: 1,
          limit: 7,
          search: this.searchTerm,
          archived: this.showArchived,
          isConfirmed: this.filterConfirmed,
        });
      } catch (err) {
        if (err?.response?.status !== 404) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.details ||
            "Errore nel caricamento dei preventivi.";
          this.errorMessage = msg;
          this.$toast.error(msg);
        }
      } finally {
        this.isLoading = false;
      }
    },

    async onResetSearch() {
      this.isLoading = true;
      try {
        this.currentPage = 1;
        this.searchTerm = "";
        await this.fetchQuotes({
          page: 1,
          limit: 7,
          archived: this.showArchived,
        });
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.details ||
          "Errore durante il reset della ricerca.";
        this.errorMessage = msg;
        this.$toast.error(msg);
      } finally {
        this.isLoading = false;
      }
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.getQuotes(page);
    },

    async getQuotes(page, search = this.searchTerm) {
      this.isLoading = true;
      try {
        const res = await this.fetchQuotes({
          page,
          limit: 7,
          search,
          archived: this.showArchived,
          isConfirmed: this.filterConfirmed,
        });
        await this.refreshArchivedFlag();
        return res;
      } catch (error) {
        if (error?.response?.status === 404 && page !== 1) {
          // prova la prima pagina
          try {
            await this.fetchQuotes({
              page: 1,
              limit: 7,
              search,
              archived: this.showArchived,
              isConfirmed: this.filterConfirmed,
            });
            this.pagination.currentPage = 1;
            return;
          } catch (_) {
            /* ignora, gestiamo sotto */
          }
        }
        if (error?.response?.status !== 404) {
          this.$nextTick(() => {
            this.errorMessage =
              error?.response?.data?.message ||
              error?.response?.data?.details ||
              "Errore nel caricamento dei preventivi.";
            this.$toast.error(this.errorMessage);
          });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async onToggleArchived(e) {
      this.currentPage = 1;
      await this.getQuotes(1);
      await this.refreshArchivedFlag();

      // chiude subito il tooltip 'sticky' perchè rimuove il focus
      if (e && e.target && typeof e.target.blur === "function") {
        e.target.blur();
      }
    },

    async refreshArchivedFlag() {
      this.hasArchived = await this.hasArchivedQuotes();
    },

    // Righe della tabella articoli, raggruppate per categoria: colonne in pdfArticoli
    buildQuoteTable(quote) {
      const lower = (v) => String(v || "").toLowerCase();

      // 1) Normalizzo tutte le righe in una lista unica
      const rows = [];
      const itemMap = new Map((quote.items || []).map((it) => [it.id, it]));

      // a) Righe da magazzino (quote.items + it.QuoteItem)
      (quote.items || []).forEach((it) => {
        const name = it.itemName || it.subBrand || it.brand || "-";
        const isTiles = lower(it.category) === "piastrelle";
        const isBattiscopa = lower(name).includes("battiscopa");

        const baseQty = Number(it?.QuoteItem?.quantity || 0);
        const sqmPerBox = Number(it.sqmPerBox || 0) || 1;
        const qty = isTiles
          ? Number((baseQty * sqmPerBox).toFixed(2))
          : baseQty;
        const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

        const unitPrice = Number(it?.QuoteItem?.price || it.price || 0);
        // [sc-48789] l'aliquota è quella della riga del preventivo, come il
        // prezzo: quella a catalogo può essere cambiata dopo, o essere stata
        // corretta a mano sulla riga. Le altre pagine leggono già così.
        const iva = it?.QuoteItem?.iva ?? it?.iva ?? 22;

        rows.push({
          group: String(it.category || "Generale")
            .trim()
            .toUpperCase(),
          code: it.itemCode || it.code || "-",
          desc: name,
          qty,
          unit,
          listPrice: it?.QuoteItem?.listPrice ?? null,
          discountedPrice: it?.QuoteItem?.discountedPrice ?? null,
          unitPrice,
          iva,
        });
      });

      // b) Righe in quote_items non presenti sopra
      (quote.quote_items || []).forEach((qi) => {
        const id = qi?.item?.id;
        if (id && itemMap.has(id)) return; // già coperto sopra

        const name = qi?.item?.itemName || "-";

        const isTiles = lower(qi?.item?.category) === "piastrelle";
        const isBattiscopa = lower(name).includes("battiscopa");

        const qty = Number(qi.quantity || 0);
        const unit = isBattiscopa ? "mL" : isTiles ? "mQ" : "pz";

        const unitPrice = Number(qi.price || 0);
        const iva = qi?.iva ?? 22;

        const group = String(qi?.item?.category || "Generale")
          .trim()
          .toUpperCase();

        rows.push({
          group,
          code: qi?.item?.itemCode || qi?.item?.code || "-",
          desc: name,
          qty,
          unit,
          listPrice: qi.listPrice ?? null,
          discountedPrice: qi.discountedPrice ?? null,
          unitPrice,
          iva,
        });
      });

      return rows;
    },

    // [sc-49299] dati del PDF: li usano la finestra di stampa e i PDF automatici
    datiPdfPreventivo(quote) {
      return datiPdf(quote, {
        cliente: clientePerPdf(quote?.Client),
        righe: this.buildQuoteTable(quote),
        totaleArticoli: quote.totalPrice,
      });
    },

    // PDF completo, senza finestra: dopo un acconto o una modifica di prezzo
    async generatePdf(quote) {
      try {
        const doc = pdfPreventivo(
          this.datiPdfPreventivo(quote),
          tuttiICampi(),
          {
            logo: logoPath,
          }
        );
        await this.caricaPdfPreventivo(doc, quote.id);
        this.$nextTick(() => {
          this.successMessage = "PDF generato e caricato con successo!";
          this.$toast.success(this.successMessage);
        });
        // aggiorna lista (così rivedi il nuovo documento nel modale)
        await this.getQuotes(this.pagination.currentPage);
      } catch (error) {
        console.error(
          "Errore durante la generazione/caricamento del PDF:",
          error
        );
        this.successMessage = "";
        this.$toast.error(
          error?.response?.data?.details ||
            error?.response?.data?.error ||
            "Errore durante la generazione/caricamento del PDF."
        );
      }
    },

    async applyModifier(quote) {
      const round2 = (n) =>
        Math.round((Number(n) + Number.EPSILON) * 100) / 100;
      if (!this.modifierType)
        return this.$toast.error("Seleziona Sconto o Acconto");

      this.confirmBusy = true;
      try {
        if (this.modifierType === "discount_percent") {
          const base = Number(quote.totalPrice) || 0; // [sc-48789]
          const percent = Number(this.discountPercent || 0);
          if (!(percent > 0 && percent <= 100)) {
            this.confirmBusy = false;
            return this.$toast.error("Percentuale non valida (0–100).");
          }

          const amount = round2(base * (percent / 100));

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "discount_percent", percent, amount }
          );

          // aggiorna UI locale
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.discount_percent = percent;
            q.discount = amount; // ora è lo sconto su (totale - acconto)
            q.realTotalPrice = ricalcolaRimanente(q); // [sc-48768]
          }

          this.$toast.success(data?.message || "Sconto aggiornato.");
          this.discountPercent = 0;
        } else if (this.modifierType === "surcharge_percent") {
          const percent = Number(this.surchargePercent || 0);
          if (!(percent > 0)) {
            this.confirmBusy = false;
            return this.$toast.error("Percentuale rincaro non valida.");
          }

          const base = Number(quote.totalPrice) || 0;
          const amount =
            Math.round((base * (percent / 100) + Number.EPSILON) * 100) / 100;

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "surcharge_percent", percent, amount }
          );

          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) {
            q.surcharge_percent = percent;
            q.surcharge = amount;
            // prima sommava il rincaro al totale ignorando sconto e acconto:
            // la colonna mostrava più del dovuto fino al refresh [sc-48768]
            q.realTotalPrice = ricalcolaRimanente(q);
          }

          this.$toast.success(data?.message || "Rincaro aggiornato.");
          this.surchargePercent = 0;
        } else if (this.modifierType === "deposit") {
          const amount = Number(this.depositAmount || 0);
          if (!(amount > 0)) {
            this.confirmBusy = false;
            return this.$toast.error("Inserisci un acconto valido.");
          }

          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${quote.id}`,
            { type: "deposit", amount }
          );

          // ✅ aggiorna la riga locale in lista
          const q = this.allQuotes.find((x) => x.id === quote.id);
          if (q) q.deposit = amount;

          this.$toast.success(data?.message || "Acconto aggiornato.");
          this.depositAmount = 0;
        }

        // azzera scelta radio
        this.modifierType = "";

        const qLocal = this.allQuotes.find((x) => x.id === quote.id);

        const needsFetch = !qLocal?.quote_items?.some((qi) => qi?.item);
        const dataForPdf = needsFetch
          ? await this.getQuotebyId(quote.id)
          : qLocal;

        await this.generatePdf(dataForPdf);
        this.closeDiscountModal();
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message || "Errore nell’applicazione."
        );
      } finally {
        this.confirmBusy = false;
      }
    },

    async getQuotebyId(quoteId) {
      try {
        const response = await axiosInstance.get(`/quote/${quoteId}`);
        // console.log("quote by id", response);
        if (response.status === 200) return response.data.quote;
        throw new Error("Errore durante il recupero del preventivo.");
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message ||
          "Impossibile caricare i dati del preventivo.";
        this.$toast.error(this.errorMessage);
      }
    },

    async onQuoteStateChange(quote, field, newValue) {
      // salva cosa stai cambiando
      this.pendingChange = { id: quote.id, field, newValue };
      this.confirmBloccato = "";

      const qid = quote?.id ?? "—"; // 👈 ID del preventivo
      const clientName = quote?.Client?.clientName ?? "cliente";

      if (field === "isConfirmed" && newValue === true) {
        this.confirmTitle = "Conferma preventivo";
        this.confirmMessage =
          `Confermare il preventivo <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Confermo…";
      } else if (
        field === "payment_state" &&
        newValue === "Pagato con Acconto"
      ) {
        const round2 = (n) =>
          Math.round((Number(n) + Number.EPSILON) * 100) / 100;
        const totale = round2(
          (Number(quote.realTotalPrice) || Number(quote.totalPrice) || 0) -
            (Number(quote.discount) || 0)
        );
        this.confirmRequiresDeposit = true;
        this.confirmDepositMethod = metodoIniziale(quote); // [sc-49299]
        this.confirmTitle = "Conferma acconto";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato con Acconto</strong> per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>.<br>` +
          `Totale dovuto: <strong>${formatPriceToPrint(totale)}</strong>.<br>` +
          `Inserisci l'importo dell'acconto ricevuto.`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "payment_state" && newValue === "Pagato") {
        const warn =
          quote.state === "Consegnato"
            ? " Confermando <strong>Pagato</strong> non potrai più modificarlo e verrà archiviato dagli attivi."
            : "";
        this.confirmTitle = "Conferma pagamento";
        this.confirmMessage =
          `Impostare il pagamento su <strong>Pagato</strong> per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Pagato";
        this.confirmBusyLabel = "Imposto…";
      } else if (field === "state" && newValue === "Consegna Parziale") {
        this.confirmTitle = "Avvia consegna parziale";
        this.confirmMessage =
          `Avviare la <strong>Consegna Parziale</strong> per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?<br>` +
          `Lo stock verrà scalato ad ogni DDT di consegna generato. ` +
          `Potrai generare più DDT di consegna fino all'ultima consegna.<br>` +
          // [sc-49300] il limite si vede prima di aprire la finestra DDT
          `Con l'acconto di <strong>${formatPriceToPrint(
            Number(quote.deposit) || 0
          )}</strong> puoi consegnare merce fino a <strong>${formatPriceToPrint(
            limiteMerceAcconto(quote.deposit)
          )}</strong> (50% dell'acconto): nel DDT seleziona solo gli articoli entro questo limite.`;
        this.confirmLabel = "Avvia consegna parziale";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "In consegna") {
        this.confirmTitle = "Manda in consegna";
        this.confirmMessage =
          `Mandare in consegna il preventivo <strong>#${qid}</strong> di ` +
          `<strong>${clientName}</strong>?<br>` +
          `Questa azione scaricherà lo stock degli articoli.`;
        this.confirmLabel = "Manda in consegna";
        this.confirmBusyLabel = "Aggiorno…";
      } else if (field === "state" && newValue === "Consegnato") {
        const warn =
          quote.payment_state === "Pagato"
            ? " Confermando <strong>Consegnato</strong> non potrai più modificarlo e verrà archiviato dagli attivi."
            : "";
        this.confirmTitle = "Conferma consegna";
        this.confirmMessage =
          `Segnare come <strong>Consegnato</strong> il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?${warn}`;
        this.confirmLabel = "Stato => Consegnato";
        this.confirmBusyLabel = "Aggiorno…";
      } else {
        this.confirmTitle = "Conferma modifica";
        this.confirmMessage =
          `Confermare la modifica dello stato per il preventivo ` +
          `<strong>#${qid}</strong> di <strong>${clientName}</strong>?`;
        this.confirmLabel = "Conferma";
        this.confirmBusyLabel = "Aggiorno…";
      }

      if (field === "state" && newValue === "Consegna Parziale") {
        await this.controllaLimiteConsegna(quote);
        // nel frattempo l'utente può aver scelto un altro stato
        if (
          this.pendingChange?.id !== quote.id ||
          this.pendingChange?.newValue !== newValue
        )
          return;
      }

      this.showConfirmModal = true;
    },

    handleConfirmCancel() {
      // Ripristina i dati UI ricaricando la pagina corrente (più semplice e sicuro)
      this.showConfirmModal = false;
      this.pendingChange = null;
      this.confirmRequiresDeposit = false;
      this.confirmDepositAmount = "";
      this.confirmDepositMethod = "";
      this.confirmBloccato = "";
      this.getQuotes(this.pagination.currentPage);
    },

    async handleConfirmOK(withDdt = false) {
      if (!this.pendingChange || this.confirmBloccato) return;
      const { id, newValue } = this.pendingChange;
      const isConsegnaParziale = newValue === "Consegna Parziale";

      if (this.confirmRequiresDeposit) {
        const amount = Number(this.confirmDepositAmount);
        if (!(amount > 0)) {
          this.$toast.error("Inserisci un importo acconto valido.");
          return;
        }
        if (!this.confirmDepositMethod) {
          this.$toast.error("Scegli il metodo di pagamento dell'acconto.");
          return;
        }
        const remaining = this.pendingQuoteRemainingBalance;
        if (amount > remaining) {
          this.$toast.error(
            `L'acconto (${this.formatPriceToPrint(
              amount
            )}) supera il residuo da saldare (${this.formatPriceToPrint(
              remaining
            )}).`
          );
          return;
        }
      }

      this.confirmBusy = true;
      let stateUpdateSuccess = false;
      try {
        if (this.confirmRequiresDeposit) {
          const round2 = (n) =>
            Math.round((Number(n) + Number.EPSILON) * 100) / 100;
          const amount = Number(this.confirmDepositAmount);
          const { data } = await axiosInstance.post(
            `quote/priceAdjustment/${id}`,
            { type: "deposit", amount, method: this.confirmDepositMethod }
          );
          const q = this.allQuotes.find((x) => x.id === id);
          if (q) {
            q.deposit = round2((Number(q.deposit) || 0) + amount);
            q.realTotalPrice = ricalcolaRimanente(q);
            // [sc-49299] il PDF generato subito dopo stampa anche questo
            if (data?.payment)
              q.payments = [...(q.payments || []), data.payment];
          }
        }
        await this.updateQuoteState(id, newValue);
        stateUpdateSuccess = true;
        if (this.confirmRequiresDeposit) {
          const qLocal = this.allQuotes.find((x) => x.id === id);
          const needsFetch = !qLocal?.quote_items?.some((qi) => qi?.item);
          const dataForPdf = needsFetch ? await this.getQuotebyId(id) : qLocal;
          await this.generatePdf(dataForPdf);
        }
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.message ||
            e?.response?.data?.details ||
            "Errore nel salvataggio."
        );
        this.getQuotes(this.pagination.currentPage);
      } finally {
        this.confirmBusy = false;
        this.showConfirmModal = false;
        this.pendingChange = null;
        this.confirmRequiresDeposit = false;
        this.confirmDepositAmount = "";
        this.confirmDepositMethod = "";
        this.confirmBloccato = "";
      }

      if (isConsegnaParziale && stateUpdateSuccess) {
        if (withDdt) {
          await this.$nextTick();
          const q = this.allQuotes.find((x) => x.id === id);
          if (q) this.openDdtModal(q);
        } else {
          this.$toast.warning(
            "Per movimentare il magazzino è necessario generare un DDT di consegna."
          );
        }
      }
    },

    async updateQuoteState(quoteId, newState) {
      this.successMessage = "";
      this.errorMessage = "";
      // const quote = await this.getQuotebyId(quoteId);

      try {
        // if (quote.payment_state === "Pagato" && newState === "Consegnato") {
        //   const ok = window.confirm(
        //     "Il preventivo è già stato pagato. Confermando 'Consegnato' non potrai più modificarlo e sparirà dagli attivi. Procedere?"
        //   );
        //   if (!ok) return this.getQuotes();
        // }
        // if (quote.state === "Consegnato" && newState === "Pagato") {
        //   const ok = window.confirm(
        //     "Il preventivo è già stato consegnato. Confermando 'Pagato' non potrai più modificarlo e sparirà dagli attivi. Procedere?"
        //   );
        //   if (!ok) return this.getQuotes();
        // }

        // const ok = window.confirm(
        //   "Sei sicuro di voler confermare questo preventivo?"
        // );
        // if (!ok) return this.getQuotes();

        const payload = JSON.stringify({ state: newState });
        const response = await this.$axios.patch(
          `quote/state/${quoteId}`,
          payload
        );

        if (response.status === 200 || response.status === 201) {
          if (newState === true) {
            this.successMessage =
              response.data.message ||
              "Il preventivo è stato confermato con successo.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Consegnato") {
            this.successMessage =
              response.data.message || "Stato aggiornato a 'Consegnato'.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Pagato") {
            this.successMessage =
              response.data.message || "Stato aggiornato a 'Pagato'.";
            this.$toast.success(this.successMessage);
          } else if (newState === "Consegna Parziale") {
            this.successMessage =
              response.data?.message || "Consegna parziale avviata.";
            this.$toast.success(this.successMessage);
          } else if (newState === "In consegna") {
            this.successMessage =
              response.data?.message || "Stato aggiornato a 'In consegna'.";
            this.$toast.success(this.successMessage);
          }

          const q = this.allQuotes.find((q) => q.id === quoteId);
          if (q) {
            if (newState === "Pagato" || newState === "Pagato con Acconto") {
              q.payment_state = newState;
            } else if (
              newState === "Consegnato" ||
              newState === "In consegna" ||
              newState === "Consegna Parziale"
            ) {
              q.state = newState;
            } else {
              q.isConfirmed = newState;
            }
            if (response.data?.quote?.Client?.lastQuote !== undefined) {
              q.lastQuote = response.data.quote.Client.lastQuote;
            }
          }

          if (q?.payment_state === "Pagato" && q?.state === "Consegnato") {
            setTimeout(async () => {
              // await this.fetchQuotes({
              //   page: this.pagination.currentPage,
              //   limit: 4,
              //   search: this.searchTerm,
              // });
              await this.refreshArchivedFlag();
              await this.getQuotes(this.pagination.currentPage);
              this.successMessage =
                "Stato aggiornato e preventivo archiviato con successo.";
              this.$toast.success(this.successMessage);
            }, 1000);
          }
        }
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data ?? {};

        if (status === 409 && data.code === "STOCK_INSUFFICIENT") {
          this.errorMessage =
            data.message || "Stock insufficiente per uno o più articoli.";
        } else if (status === 409) {
          this.errorMessage =
            data.details ||
            data.message ||
            `Lo stato del preventivo con ID '${quoteId}' è già '${newState}'`;
        } else {
          this.errorMessage =
            data.details || data.error || "Errore durante l'aggiornamento.";
        }

        this.$toast.error(this.errorMessage);
        this.getQuotes();
      }
    },

    /* ---- utilità stato preventivo ---- */
    isCompleted(quote) {
      // consideriamo "completato" quando è Pagato + Consegnato
      return quote?.payment_state === "Pagato" && quote?.state === "Consegnato";
    },

    canRestore(quote) {
      // negli archiviati non mostriamo il ripristino se è già completato
      return !this.isCompleted(quote);
    },

    canEdit(quote) {
      // se sto guardando gli archiviati e il preventivo è completato => niente modifica
      return !(
        this.showArchived &&
        (this.canRestore(quote) || this.isCompleted(quote))
      );
    },
  },

  watch: {
    $route(to, from) {
      const toName = to?.name;
      const fromName = from?.name; // può essere undefined al primo giro
      if (toName === "quotes" && fromName !== "quotes") {
        this.getQuotes(this.currentPage);
      }
    },
    searchTerm() {
      this.onSearch();
    },
    // "$route.name"(toName) {
    //   if (toName === "quotes") this.getQuotes(this.currentPage);
    // },
  },

  async mounted() {
    await this.refreshArchivedFlag();
    this.getQuotes(this.currentPage);

    document.addEventListener("click", this.handleDocumentClick, {
      passive: true,
    });
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleDocumentClick);
  },
};
</script>

<style src="@/css/QuotePage.css"></style>
